import {Input, Modal} from 'antd'
import {
    organization_getFeatures_Api,
    organization_requestFeatureVerification_Api,
    organization_toggleFeature_Api
} from 'apis'
import {CustomToggleField} from 'components/form/fields/ToggleField'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useState} from 'react'
import classNames from 'classnames'
import dayjs from 'dayjs'
import {HiOutlineInformationCircle, HiOutlineShieldCheck, HiOutlineShieldExclamation} from 'react-icons/hi2'
import LoadingBlock from '../../Loading/LoadingBlock'
import {renderIf} from '../../Shared'
import WorkspaceContext from '../WorkspaceContext'

// Owner-only: bật/tắt "module lớn" (Đào tạo & Huấn luyện, Sổ sách & Bí tích, Tài Chính & Bác Ái,
// Học & Chơi, Cửa hàng) cho cả tổ chức. Nhãn/mô tả/điều kiện đến từ BE (đã __() sẵn theo
// config/organization_features.php) - FE chỉ hiển thị, không tự dịch lại.
// Route đã gate bằng organizationManageFeatures (owner_only) - xem WorkspaceSettingContent.js.
//
// Nền tảng (system admin) có thể OVERRIDE lựa chọn của owner (platform_status - xem
// OrganizationModuleSetting::effectiveIsEnabled() phía BE) - is_enabled trả về từ API đã là giá
// trị HIỆU LỰC (đã tính override), owner_choice là lựa chọn riêng của owner để đối chiếu.

const PLATFORM_STATUS = {
    ALLOWED: 'allowed',
    FORCED_ENABLED: 'forced_enabled',
    FORCED_DISABLED: 'forced_disabled',
}

// Badge trạng thái từ nền tảng - LUÔN hiển thị (kể cả "allowed") để owner biết đây không phải mục
// bị lỗi/thiếu tải, mà nền tảng đang chủ động cho phép tự quản.
const PlatformStatusBadge = ({platformStatus, platformNote}) => {
    const t = useTranslate()
    const isOverridden = platformStatus !== PLATFORM_STATUS.ALLOWED
    const isForcedOn = platformStatus === PLATFORM_STATUS.FORCED_ENABLED

    return (
        <div
            className={classNames(
                'mt-2 p-2 rounded-lg flex items-start gap-2 text-2xs tracking-wide',
                !isOverridden && 'background-100 text-color-300',
                isOverridden && isForcedOn && 'bg-blue-50 text-blue-600',
                isOverridden && !isForcedOn && 'bg-red-50 text-red-600',
            )}>
            {isOverridden
                ? <HiOutlineShieldExclamation size={14} className="flex-shrink-0 mt-0.5"/>
                : <HiOutlineShieldCheck size={14} className="flex-shrink-0 mt-0.5"/>}
            <div className="flex-1 space-y-0.5">
                <div className="font-semibold">
                    {t('platform status label')}: {' '}
                    {isOverridden
                        ? t(isForcedOn ? 'platform status forced enabled' : 'platform status forced disabled')
                        : t('platform status allowed')}
                </div>
                {renderIf(isOverridden && platformNote, <div className="italic">{platformNote}</div>)}
            </div>
        </div>
    )
}

// Owner gửi yêu cầu kích hoạt kèm ghi chú/thông tin ưu tiên liên hệ - thay cho modal cũ chỉ hiện
// email/sđt platform thụ động (owner phải tự đi liên hệ ngoài luồng app, nền tảng không biết gì).
const RequestVerificationModal = ({open, onClose, label, onSubmit, isLoading}) => {
    const t = useTranslate()
    const [note, setNote] = useState('')

    return (
        <Modal
            title={t('request feature activation title', {feature: label})}
            open={open}
            onCancel={onClose}
            onOk={() => onSubmit(note)}
            okText={t('send request')}
            cancelText={t('cancel')}
            confirmLoading={isLoading}
            destroyOnHidden>
            <div className="flex flex-col gap-2">
                <p className="text-color-300 tracking-wide">
                    {t('request feature activation description')}
                </p>
                <Input.TextArea
                    rows={3}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={t('request feature activation note placeholder')}
                />
            </div>
        </Modal>
    )
}

const FeatureToggleItem = ({feature, organizationId, onToggled}) => {
    const t = useTranslate()
    const [requestOpen, setRequestOpen] = useState(false)
    const {
        feature_key, label, description, condition, is_enabled, owner_choice,
        can_enable, requires_platform_verification, verification_status, coming_soon,
        platform_status, platform_note, is_overridden,
        has_pending_verification_request, verification_requested_at, verification_contact_note,
    } = feature

    const {handleAsyncAction, isLoading} = useAsyncAction({
        apiInfo: organization_toggleFeature_Api,
        query: {':id': organizationId, ':feature_key': feature_key},
        onSuccess: onToggled,
    })

    const {handleAsyncAction: sendVerificationRequest, isLoading: isSendingRequest} = useAsyncAction({
        apiInfo: organization_requestFeatureVerification_Api,
        query: {':id': organizationId, ':feature_key': feature_key},
        onSuccess: () => {
            setRequestOpen(false)
            onToggled()
        },
    })

    const needsPlatformContact = requires_platform_verification && verification_status !== 'verified'

    // Không disable Switch cho trường hợp chưa đủ điều kiện - để owner bấm được và NHẬN THÔNG BÁO rõ
    // ràng (kèm thông tin liên hệ nếu cần), thay vì 1 công tắc xám mờ không rõ lý do. Chỉ thực sự
    // TẮT tương tác khi request đang chạy HOẶC nền tảng đang override (owner không đổi được nữa,
    // API cũng chặn - xem OrganizationFeatureController::toggle()).
    const handleToggleAttempt = (nextValue) => {
        if (coming_soon) return // Switch đã disabled - không cần xử lý thêm

        if (is_overridden) {
            Modal.info({
                title: t('feature controlled by platform title', {feature: label}),
                content: platform_note || t('platform status label'),
            })
            return
        }

        if (nextValue && !can_enable) {
            if (needsPlatformContact) {
                if (has_pending_verification_request) {
                    // Đã gửi rồi - chỉ nhắc lại thông tin đã gửi, không cho gửi trùng (chặn cả ở
                    // BE nếu bấm được qua cách khác - xem OrganizationFeatureController::requestVerification).
                    Modal.info({
                        title: t('verification request already sent title', {feature: label}),
                        content: (
                            <div className="flex flex-col gap-1">
                                <span>{t('verification request sent at')}: {verification_requested_at ? dayjs(verification_requested_at).format('DD/MM/YYYY HH:mm') : '—'}</span>
                                {verification_contact_note && <span className="italic">"{verification_contact_note}"</span>}
                            </div>
                        ),
                    })
                } else {
                    setRequestOpen(true)
                }
            } else {
                Modal.warning({
                    title: t('cannot enable feature title', {feature: label}),
                    content: condition || t('this feature is not available for your organization'),
                })
            }
            return
        }

        Modal.confirm({
            title: t(nextValue ? 'enable feature confirm title' : 'disable feature confirm title', {feature: label}),
            content: t(nextValue ? 'enable feature confirm content' : 'disable feature confirm content', {feature: label}),
            okText: t(nextValue ? 'enable' : 'disable'),
            cancelText: t('cancel'),
            onOk: () => handleAsyncAction({enabled: nextValue ? 1 : 0}),
        })
    }

    return (
        <div className="p-3 border border-color-100 background rounded-xl">
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                    <CustomToggleField
                        title={label}
                        description={description}
                        value={is_enabled}
                        disabled={isLoading || is_overridden || coming_soon}
                        onChange={handleToggleAttempt}
                        className="p-0 border-0"
                    />
                </div>
                {renderIf(
                    coming_soon,
                    <span
                        style={{padding: '4px 8px'}}
                        className="flex-shrink-0 bg-yellow-50 font-medium text-2xs text-yellow-400 italic leading-none rounded">
                        {t('coming soon')}
                    </span>
                )}
            </div>
            {/* Chỉ hiện điều kiện khi CHƯA thỏa VÀ CHƯA bật (!is_enabled && !can_enable). Thêm
                !is_enabled vì có trường hợp is_enabled=true (đã BẬT thật, kể cả do nền tảng bật hộ)
                nhưng can_enable tính riêng vẫn false (verification_status của org chưa kịp cập nhật
                'verified' dù thực tế đã được cho phép) - lúc đó KHÔNG được nhắc "cần liên hệ" nữa vì
                đã đang hoạt động rồi, nhắc vậy gây hiểu lầm. */}
            {renderIf(
                condition && !is_enabled && !can_enable,
                <div className="mt-2 pt-2 border-t border-dashed border-color-100 flex items-start gap-2 text-2xs text-secondary-500">
                    <HiOutlineInformationCircle size={14} className="flex-shrink-0 mt-0.5"/>
                    <span className="flex-1 italic tracking-wide">{condition}</span>
                </div>
            )}
            {renderIf(
                is_overridden && owner_choice !== is_enabled,
                <div className="mt-1 ml-1 text-2xs text-color-300 italic">
                    {t('your own choice was')}: {t(owner_choice ? 'enable' : 'disable')}
                </div>
            )}
            {/* !is_enabled - cùng lý do với khối condition phía trên, tránh nhắc "đang chờ xử lý"
                khi feature thật ra đã hoạt động rồi (is_enabled=true do nền tảng bật hộ). */}
            {renderIf(
                has_pending_verification_request && !is_enabled,
                <div className="mt-2 pt-2 border-t border-dashed border-color-100 flex items-start gap-2 text-2xs text-blue-500">
                    <HiOutlineInformationCircle size={14} className="flex-shrink-0 mt-0.5"/>
                    <span className="flex-1 italic tracking-wide">
                        {t('verification request pending note', {date: verification_requested_at ? dayjs(verification_requested_at).format('DD/MM/YYYY HH:mm') : '—'})}
                    </span>
                </div>
            )}
            <PlatformStatusBadge platformStatus={platform_status} platformNote={platform_note}/>
            <RequestVerificationModal
                open={requestOpen}
                onClose={() => setRequestOpen(false)}
                label={label}
                isLoading={isSendingRequest}
                onSubmit={(note) => sendVerificationRequest({note})}
            />
        </div>
    )
}

const WorkspaceOrganizationFeatures = () => {
    const t = useTranslate()
    const {organization} = useContext(WorkspaceContext)
    const organizationId = organization?.id

    const {result: features, isLoading, handleAsyncAction: refetch} = useAsync({
        apiInfo: organization_getFeatures_Api,
        query: {':id': organizationId},
        deps: [organizationId],
    })

    if (isLoading && !features) return <LoadingBlock/>

    return (
        <div className="max-w-3xl mx-auto p-0 md:p-3 space-y-3">
            <div className="mb-2">
                <h2 className="text-lg font-bold text-color-000 tracking-wide">
                    {t('enable disable features')}
                </h2>
                <p className="text-color-300 tracking-wide">
                    {t('enable disable features description')}
                </p>
                <p className="text-color-300 tracking-wide">
                    {t('enable disable features usage note')}
                </p>
            </div>
            <div className="space-y-3">
                {(features || []).map(feature => (
                    <FeatureToggleItem
                        key={feature.feature_key}
                        feature={feature}
                        organizationId={organizationId}
                        onToggled={refetch}
                    />
                ))}
            </div>
        </div>
    )
}

export default WorkspaceOrganizationFeatures
